/**
 * Create the store with asynchronously loaded reducers
 */

import get from 'lodash/get';
import has from 'lodash/has';
import isPlainObject from 'lodash/isPlainObject';
import { applyMiddleware, compose, createStore } from 'redux';

import { logError } from 'containers/ErrorBoundary/actions';
import { createBrowserHistory } from 'history';
import { fromJS } from 'immutable';
import { assoc, pipe } from 'ramda';
import reduxCatch from 'redux-catch';
import { createReduxHistoryContext } from 'redux-first-history';
import createSagaMiddleware from 'redux-saga';
import { serializeError } from 'serialize-error';
import createReducer from './reducers';
import rootSaga from './rootSaga';

const { createReduxHistory, routerMiddleware, routerReducer } =
  createReduxHistoryContext({
    history: createBrowserHistory(),
    routerReducerKey: 'router',
    selectRouterState: (state) => state.get('router'),
  });

const errorReducerHandler = (error, _getState, lastAction, dispatch) => {
  if (process.env.NODE_ENV === 'development') {
    const errorFunction = console.error ?? console.log;
    errorFunction(error);
  }
  dispatch(
    logError({
      error: `error: ${get(serializeError(error), 'stack', error)}, last action: ${get(lastAction, 'type')}`,
      origin: 'REDUCER',
    }),
  );
};

const clearPayload = (payload) => {
  if (payload instanceof Event) {
    return null;
  }
  return Object.keys(payload).reduce((acc, key) => {
    const item = payload[key];

    if (typeof item === 'function' || (item && item.__proto__ !== undefined)) {
      return acc;
    }
    acc[key] = item;
    return acc;
  }, {});
};

const actionEventHandler = (store) => (next) => (action) => {
  if (action.payload instanceof Event || has(action, 'payload.nativeEvent')) {
    store.dispatch(
      logError({
        error: `Found event in action ${action.type} REPORT IT TO DEVELOP TEAM`,
        origin: 'action',
      }),
    );
    next(assoc('payload', clearPayload(action.payload), action));
  } else if (
    isPlainObject(action.payload) &&
    Object.keys(action.payload).find(
      (key) =>
        action.payload[key] instanceof Event ||
        has(action, ['payload', key, 'nativeEvent']),
    )
  ) {
    const newPayload = Object.keys(action.payload).reduce((acc, key) => {
      if (
        action.payload[key] instanceof Event ||
        has(action, ['payload', key, 'nativeEvent'])
      ) {
        return assoc(key, null, acc);
      }
      return acc;
    }, action.payload);
    next(assoc('payload', newPayload, action));
    store.dispatch(
      logError({ error: `Event in action ${action.type}`, origin: 'action' }),
    );
  } else {
    next(action);
  }
};

const sagaMiddleware = createSagaMiddleware();
// Create the store with two middlewares
// 1. sagaMiddleware: Makes redux-sagas work
// 2. routerMiddleware: Syncs the location/URL path to the state
const middlewares = pipe((wares) =>
  process.env.NODE_ENV === 'development'
    ? wares.concat(actionEventHandler)
    : wares,
)([sagaMiddleware, routerMiddleware, reduxCatch(errorReducerHandler)]);

const enhancers = [applyMiddleware(...middlewares)];

// If Redux DevTools Extension is installed use it, otherwise use Redux compose
const composeEnhancers =
  process.env.NODE_ENV !== 'production' &&
  typeof window === 'object' &&
  window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    : compose;

const reducers = createReducer(routerReducer);
export const store = createStore(
  reducers,
  fromJS({}),
  composeEnhancers(...enhancers),
);

// Extensions
store.runSaga = sagaMiddleware.run;
store.asyncReducers = {}; // Async reducer registry
store.runSaga(rootSaga);

export const history = createReduxHistory(store);
